import {computed, effectScope, watch, ref} from 'vue';
import {canUseTappy} from '~/utils/tappy';
import {useRoute, useRouter} from "vue-router";
import {useCookie, useState, useNuxtApp} from '#app';
import {useGoogleRecaptcha} from "~/composables/useGoogleRecaptcha";
import {endpoints} from "~/network/endpoints.js";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {useMessagingSignalR} from "~/composables/useMessagingSignalR";
import {useStripeConnect} from "~/composables/useStripeConnect";

import {MFA_DEVICE_HEADER, readDeviceToken} from '~/utils/mfa';
import {sessionUserCookieValue} from '~/utils/sessionUser';
import {visitorPreviewFlag, visitorPreviewHeaders} from '~/composables/useVisitorPreview';

// One auth service per Nuxt app. On the server that is one per request (a
// fresh nuxtApp is created for every render); in the browser the app is a
// singleton, so one for the session. Before this, every component and every
// request helper (useHttpRequest, useMessaging, useSafety, useFollows...)
// built its own copy: 4 cookie refs, 10 useCrudApi instances, the computeds
// and watchers, 5,647 times in one signed-in club page render (MEMORY_M4,
// 2026-10-20: ~350 MB of the 440 MB that render held). The copy is kept on
// the nuxtApp itself, never in module scope, because the Nitro worker shares
// this module between every request it renders at once (see the ssr-crud
// shared-auth leak, X37); a module-level instance would be another visitor's.
const AUTH_SERVICE_KEY = '_authService';

export function useAuthService() {
    const nuxtApp = useNuxtApp();
    return nuxtApp[AUTH_SERVICE_KEY] ??= createAuthService(nuxtApp);
}

function createAuthService(nuxtApp) {
    // Detached: the refs, computeds, watchers and cookie refs below belong to
    // the app (the request, on the server), not to whichever component asked
    // first. In a component's scope they would be stopped with that component
    // and every later caller would read dead state.
    const scope = effectScope(true);
    return scope.run(() => buildAuthService(nuxtApp, scope));
}

function buildAuthService(nuxtApp, scope) {
    // The visitor preview's flag (useVisitorPreview): this app's, read by the Crud headers generator and the 401 hook.
    const visitorPreview = visitorPreviewFlag(nuxtApp);
    // `nuxtApp` is named on every Crud auth-hook setter below: the hooks are
    // kept per request on the server, and after an await the Nuxt context is
    // no longer implicit there.
    const router = useRouter();
    const route = useRoute();

    const {setAlertMessage} = useAlertService();

    // SignalR instance (only on client)
    const signalR = process.client ? useMessagingSignalR() : null;

    const {executeRecaptcha} = useGoogleRecaptcha();
    // Using useCookie to store tokens securely
    // Session cookies live 7 days from the LAST server-rendered visit, not from
    // sign-in: init() re-issues them on the server (see touchSessionCookies), and
    // `refresh` makes Nuxt send the Set-Cookie header even though the value is
    // unchanged. Server-set matters — Safari and Brave cap script-written
    // cookies at 7 days and purge them after 7 days without a visit, but honour
    // an HTTP Set-Cookie in full.
    const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
    const sessionCookieOptions = {maxAge: SESSION_COOKIE_MAX_AGE, refresh: true};
    const userCookie = useCookie('user', sessionCookieOptions);
    const accessTokenCookie = useCookie('accessToken', sessionCookieOptions);
    const impersonatingUserCookie = useCookie('impersonatingUser', sessionCookieOptions);
    const impersonatingAccessTokenCookie = useCookie('impersonatingAccessToken', sessionCookieOptions);

    // Re-issue every present session cookie with a full max-age. Assigning the
    // same value flags it as changed for Nuxt's server cookie writer, which then
    // emits Set-Cookie at render time; on the client this is a no-op by design.
    const touchSessionCookies = () => {
        if (!import.meta.server) return;
        for (const cookie of [userCookie, accessTokenCookie, impersonatingUserCookie, impersonatingAccessTokenCookie]) {
            if (cookie.value) cookie.value = cookie.value;
        }
        // An older sign-in wrote the whole user; re-issue it slim (sessionUser.js).
        // The impersonation copy gets the same treatment (X211).
        if (userCookie.value) userCookie.value = sessionUserCookieValue(userCookie.value);
        if (impersonatingUserCookie.value) impersonatingUserCookie.value = sessionUserCookieValue(impersonatingUserCookie.value);
    };

    const _user = useState('user', () => userCookie.value || null);
    const user = computed(() => _user.value ?? {});
    // Whether this session holds a user READ FROM THE API rather than the copy
    // the cookie started it with. updateLocalUser swallows a failed refresh on
    // purpose (an unreachable API must not log anyone out), so without this flag
    // nothing downstream can tell the two apart — and the required-prompt wall
    // turns on `pendingFormPrompt`, a field only the refresh can add.
    const userRefreshed = useState('userRefreshed', () => false);
    // True while this session holds a token the API could not be asked about:
    // /auth/me failed WITHOUT a 401 (API restarting, network blip, 5xx). The
    // cookie only says who is signed in (sessionUser.js), not which clubs they
    // run, so the route guards must not judge the session on it: auth.js
    // renders the page instead of bouncing to /admin or /login (the
    // api-restart-logout rule; X211). The next successful /auth/me clears it.
    const authUnreachable = useState('authUnreachable', () => false);
    const accessToken = useState('accessToken', () => accessTokenCookie.value || null);
    const impersonatingUser = useState('impersonatingUser', () => impersonatingUserCookie.value || null);
    const impersonatingAccessToken = useState('impersonatingAccessToken', () => impersonatingAccessTokenCookie.value || null);

    // The sign-in, registration, verification and impersonation requests each
    // have a useCrudApi of their own (19 refs, 6 computeds, ~30 closures). They
    // are built the first time something reaches for them: a sign-in page
    // destructuring loginErrors in its setup, or login() itself. A page render
    // that only reads the user builds none of them. Built inside this service's
    // scope so they live as long as it does.
    const lazyCrud = (path) => {
        let api = null;
        return () => api ??= scope.run(() => useCrudApi(path));
    };
    const forgotPasswordApi = lazyCrud('api/v2/auth/forgot-password');
    const loginApi = lazyCrud('api/v2/auth/login');
    const loginVerificationApi = lazyCrud('api/v2/auth/login/verification');
    const registerVerificationApi = lazyCrud('api/v2/auth/register/verification');
    const externalLoginApi = lazyCrud('api/v2/auth/external-login');
    const externalLoginLinkApi = lazyCrud('api/v2/auth/external-login-link');
    const externalLoginDeleteApi = lazyCrud('api/v2/auth/external-login');
    const registerApi = lazyCrud('api/v2/auth/me');
    const impersonateApi = lazyCrud('api/v2/users');

    // Join with invite link state
    const joinPending = ref(false);
    const joinError = ref(null);

    // auth/me is read by init on every signed-in render and by updateLocalUser
    // after a sign-in, a proof or an impersonation, some of those from event
    // handlers: built once with the service.
    const {item: meItem, getItem: getMe} = useCrudApi('api/v2/auth/me');

    const init = async () => {
        // In the browser the state arrives from the server render with the
        // whole user /auth/me gave it; the cookie is only who that is. Keep the
        // whole user while the cookie still names them, so a client whose own
        // /auth/me then fails holds the last good user, not the slim one
        // (X211). A different or missing cookie starts from the cookie as before.
        if (!sameSessionUser(_user.value, userCookie.value)) {
            _user.value = userCookie.value || null;
        }
        accessToken.value = accessTokenCookie.value || null;
        impersonatingUser.value = impersonatingUserCookie.value || null;
        impersonatingAccessToken.value = impersonatingAccessTokenCookie.value || null;
        updateAuthHeaders();
        // Registered before the first authenticated call so a 401 on auth/me
        // (an expired or revoked token) signs THIS request out: on the server
        // that clears its auth state and cookies and the page renders signed
        // out or redirects to sign-in, instead of carrying on with a dead
        // token. The module runs it inside this request's context.
        setCrudOnUnauthenticated(() => {
            // In the visitor preview the API reads no token, so a 401 is a signed-in-only call the preview
            // should not have made, never the session going (useVisitorPreview).
            if (visitorPreview.value) return;
            logout();
        }, nuxtApp);
        // A token without its user cookie: the session the oversized cookie
        // lost (sessionUser.js). Ask the API who the token belongs to; a valid
        // token brings the user (and a cookie that fits) back, a dead one
        // answers 401 and the hook above signs out as before.
        if (accessToken.value && !_user.value) {
            await updateLocalUser();
            if (isAuthenticated.value) {
                userCookie.value = sessionUserCookieValue(_user.value);
                touchSessionCookies();
            }
        } else if (isAuthenticated.value) {
            touchSessionCookies();
            await updateLocalUser();
        }
    };
    const getAccessToken = () => {
        if (impersonatingAccessToken.value) {
            return impersonatingAccessToken.value;
        } else if (accessToken.value) {
            return accessToken.value;
        }
        return null;
    }

    const updateLocalUser = async () => {
        // Cleared first: a failed request leaves the last answer in the item
        // (an HTTP error) or empties it (a thrown fetch); either way only an
        // answer to THIS request may count as the user.
        meItem.value = null;
        try {
            await getMe("");
        } catch (e) {
            // Unreachable API (restart, network blip) must not destroy the session
        }
        // Only replace the cached user when the refresh actually returned one —
        // assigning null here wipes the user cookie and silently logs out on the
        // next route. A genuine 401 still logs out via setCrudOnUnauthenticated
        // (which runs during getMe and leaves meItem at its empty default: an
        // object with no id, which must not become the user either).
        if (meItem.value && meItem.value.id) {
            _user.value = meItem.value;
            userRefreshed.value = true;
            authUnreachable.value = false;
        } else {
            // No user came back. A 401 has already signed out (the token is
            // gone); a token still here is a session the API could not answer for.
            authUnreachable.value = !!accessToken.value;
        }
    }

    const updateAuthHeaders = () => {
        // useCookie works on both server and client in Nuxt 3
        // On server: reads from request headers
        // On client: reads from document.cookie
        // Guarded: on the server this also runs after an await (the 401
        // sign-out path, which the Crud module runs inside this request's
        // context). Should the context ever be missing, a lost timezone header
        // must cost nothing more than the header.
        let timezone = null;
        try {
            timezone = useCookie('timezone').value || null;
        } catch {
            timezone = null;
        }

        // Only include timezone header if we actually have a timezone value
        const baseHeaders = {};
        // Each generator runs as a request goes out, so it adds X-View-As while a page previews as a visitor
        // (useVisitorPreview, X73): every useCrudApi call, crud-list-loader included, is then served signed out.
        if (impersonatingAccessToken.value) {
            setCrudHeadersGenerator(() => ({
                ...baseHeaders,
                Authorization: `Bearer ${impersonatingAccessToken.value}`,
                ...visitorPreviewHeaders(visitorPreview),
            }), nuxtApp);
        } else if (accessToken.value) {
            if (timezone) {
                baseHeaders['X-Timezone-Identifier'] = timezone;
            }
            // A browser remembered after a two-step sign-in says so on every
            // call (MFA_PLAN.md); the API ignores it for anyone else's account.
            const device = process.client ? readDeviceToken() : null;
            if (device) baseHeaders[MFA_DEVICE_HEADER] = device;
            setCrudHeadersGenerator(() => ({
                ...baseHeaders,
                Authorization: `Bearer ${accessToken.value}`,
                ...visitorPreviewHeaders(visitorPreview),
            }), nuxtApp);
        } else {
            setCrudHeadersGenerator(() => ({
                ...baseHeaders,
                ...visitorPreviewHeaders(visitorPreview),
            }), nuxtApp);
        }
    };

    const login = async (credentials) => {
        const api = loginApi();
        await api.createItem(credentials);
        const result = api.item.value;
        if (result) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = sessionUserCookieValue(result);
        }
        updateAuthHeaders();
    };

    const externalLoginDelete = async (provider) => {
        const api = externalLoginDeleteApi();
        await api.deleteItem(provider);
        const result = api.item.value;
        if (result && result.accessToken) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = sessionUserCookieValue(result);
            updateAuthHeaders();
        }

    }

    // A two-step verification handed back a token carrying the proof: use it
    // from now on, and refresh the user so mfaVerified reads true.
    const adoptProof = async (token) => {
        if (!token) return;
        accessToken.value = token;
        accessTokenCookie.value = token;
        updateAuthHeaders();
        await updateLocalUser();
    };

    // Switch the session to a signed-in user shape (e.g. the kept account after a merge).
    const adoptSession = (result) => {
        if (!result || !result.accessToken) return;
        accessToken.value = result.accessToken;
        _user.value = result;
        accessTokenCookie.value = result.accessToken;
        userCookie.value = sessionUserCookieValue(result);
        updateAuthHeaders();
    };

    const externalLoginLink = async (credentials) => {
        const api = externalLoginLinkApi();
        await api.createItem(credentials);
        const result = api.item.value;
        if (result && result.accessToken) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = sessionUserCookieValue(result);
            updateAuthHeaders();
        }

    };

    const externalLogin = async (credentials) => {
        const api = externalLoginApi();
        await api.createItem(credentials);
        const result = api.item.value;
        if (result && result.accessToken) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = sessionUserCookieValue(result);
        }
        updateAuthHeaders();
    };

    const joinWithInviteLink = async (inviteLinkId, authId, message, extra = {}) => {
        joinPending.value = true;
        joinError.value = null;
        try {
            const {joinInviteLink} = useInviteLink();
            const result = await joinInviteLink(inviteLinkId, authId, message, extra);
            if (result && result.accessToken) {
                accessToken.value = result.accessToken;
                _user.value = result;
                // Store in cookies
                accessTokenCookie.value = result.accessToken;
                userCookie.value = sessionUserCookieValue(result);
                updateAuthHeaders();
            }
            return result;
        } catch (e) {
            joinError.value = e;
            throw e;
        } finally {
            joinPending.value = false;
        }
    };

    const validateShortLivedToken = async (token) => {
        const {httpRequest} = useHttpRequest(); // Access the HTTP request utility
        let result = null;
        try {
            // Perform the network request to validate the token
            result = await httpRequest(
                endpoints.auth.validateShortLivedToken(),
                {"token": token},
            );

            if (result && result.user) {
                const user = result.user;
                accessToken.value = user.accessToken;
                _user.value = user;
                accessTokenCookie.value = user.accessToken;
                userCookie.value = sessionUserCookieValue(user);
                updateAuthHeaders();
            }
            return result;
        } catch (error) {
            console.error("Error validating short-lived token:", error);
            throw error;
        }
    };

    const impersonateUser = async (userId) => {
        const api = impersonateApi();
        await api.getItem(userId + "/impersonate");
        const result = api.item.value;
        api.itemPending.value = true;
        if (result) {
            impersonatingAccessToken.value = result;
            impersonatingAccessTokenCookie.value = result;
            updateAuthHeaders();
            await updateLocalUser();
            await router.replace({path: route.path, force: true});
        }
        api.itemPending.value = false;
    };

    const stopImpersonating = async () => {
        impersonatingAccessToken.value = null;
        impersonatingUser.value = null;
        updateAuthHeaders();
        await updateLocalUser();
        await router.replace({path: route.path, force: true});
    };
    const forgotPassword = async (data) => {
        const {token} = await executeRecaptcha("forgotPassword");
        data.RecaptchaResponse = token;
        await forgotPasswordApi().createItem(data);
    };
    const register = async (credentials) => {
        const {token} = await executeRecaptcha("register");
        credentials.RecaptchaResponse = token;
        await registerApi().createItem(credentials);
    };

    const logout = async () => {
        // Disconnect SignalR first and WAIT for it: stop() sends the hub close
        // handshake that marks the connection disconnected server-side. Fired
        // un-awaited, navigation/reload can kill it mid-flight and the server
        // keeps the user "online" until the stale-connection cleanup.
        if (process.client && signalR) {
            try {
                await signalR.disconnect();
            } catch (err) {
                console.warn('SignalR disconnect during logout failed:', err);
            }
        }

        // Stripe embedded components hold their own user session per club
        // account — Stripe asks us to invalidate it on app logout (and only then).
        if (process.client) {
            try {
                useStripeConnect().logoutAll();
            } catch (err) {
                console.warn('Stripe Connect logout failed:', err);
            }
        }

        _user.value = null;
        accessToken.value = null;
        impersonatingUser.value = null;
        impersonatingAccessToken.value = null;
        authUnreachable.value = false;

        // Clear cookies
        userCookie.value = null;
        accessTokenCookie.value = null;
        impersonatingUserCookie.value = null;
        impersonatingAccessTokenCookie.value = null;
        // On the server (a 401 during the render) the refs above belong to this
        // service instance; every later instance in the same render makes its
        // own refs from the request's cookie header and, with `refresh`, writes
        // that value back, so the dead cookies would be re-issued for another
        // 7 days. Take them out of the request itself so the rest of the render
        // starts signed out and the response clears them.
        if (import.meta.server) {
            dropAuthCookiesFromRequest();
        }
        updateAuthHeaders();

    };

    const AUTH_COOKIE_NAMES = ['user', 'accessToken', 'impersonatingUser', 'impersonatingAccessToken'];
    const dropAuthCookiesFromRequest = () => {
        try {
            const event = nuxtApp.ssrContext?.event;
            const headers = event?.node?.req?.headers;
            if (!headers || !headers.cookie) return;
            const present = AUTH_COOKIE_NAMES.filter((name) => headers.cookie.split(';').some((part) => part.trim().startsWith(name + '=')));
            if (!present.length) return;
            headers.cookie = headers.cookie
                .split(';')
                .map((part) => part.trim())
                .filter((part) => part && !AUTH_COOKIE_NAMES.some((name) => part.startsWith(name + '=')))
                .join('; ');
            // Nuxt only writes a deletion for a cookie the request still carries,
            // so with the header stripped the browser would keep the dead ones
            // until the client signed out. Clear them on the response here (the
            // same shape h3's deleteCookie writes; the cookies are set on '/').
            const res = event.node?.res;
            if (!res || res.headersSent) return;
            const existing = res.getHeader('set-cookie');
            const list = Array.isArray(existing) ? [...existing] : existing ? [String(existing)] : [];
            for (const name of present) list.push(`${name}=; Max-Age=0; Path=/`);
            res.setHeader('set-cookie', list);
        } catch {
            // Never let a sign-out fail on a header write.
        }
    };

    watch(_user, (newVal) => {
        if (newVal) {
            userCookie.value = sessionUserCookieValue(newVal);
        } else {
            userCookie.value = null;
        }
    });

    watch(accessToken, (newVal) => {
        if (newVal) {
            accessTokenCookie.value = newVal;
        } else {
            accessTokenCookie.value = null;
        }
    });

    // Only who, never the whole user, like the `user` cookie (X211).
    watch(impersonatingUser, (newVal) => {
        if (newVal) {
            impersonatingUserCookie.value = sessionUserCookieValue(newVal);
        } else {
            impersonatingUserCookie.value = null;
        }
    });

    watch(impersonatingAccessToken, (newVal) => {
        if (newVal) {
            impersonatingAccessTokenCookie.value = newVal;
        } else {
            impersonatingAccessTokenCookie.value = null;
        }
    });

    const isAuthenticated = computed(() => _user.value !== null && accessToken.value !== null);

    // Manage SignalR connection based on authentication state (client-side only)
    // Use a shared state to ensure watcher is only created once
    const signalRWatcherInitialized = useState('signalR-watcher-initialized', () => false);

    if (process.client && signalR && !signalRWatcherInitialized.value) {
        signalRWatcherInitialized.value = true;

        watch(isAuthenticated, async (authenticated, wasAuthenticated) => {
            if (authenticated && !wasAuthenticated) {
                // User just logged in - connect SignalR
                console.log('User authenticated, connecting SignalR...');
                try {
                    await signalR.connect('/signalr/v1');
                } catch (error) {
                    console.error('Failed to connect SignalR:', error);
                }
            } else if (!authenticated && wasAuthenticated) {
                // User just logged out - disconnect SignalR
                console.log('User logged out, disconnecting SignalR...');
                signalR.disconnect();
            }
        }, { immediate: true }); // Run immediately to connect if already authenticated

        // Impersonation swaps the effective identity but the socket keeps the identity
        // it authenticated with — leaving it connected as the admin while the UI (and
        // IndexedDB scoping) is the impersonated user leaks the admin's live messages
        // into the other account's local cache. Reconnect so the hub identity, user
        // routing and conversation groups all match the effective user.
        watch(impersonatingAccessToken, async (newToken, oldToken) => {
            if (newToken === oldToken || !isAuthenticated.value) return;
            console.log('Impersonation changed, reconnecting SignalR with effective identity...');
            try {
                await signalR.disconnect();
                await signalR.connect('/signalr/v1');
            } catch (error) {
                console.error('Failed to reconnect SignalR after impersonation change:', error);
            }
        });
    }

    const isSuperUser = computed(() => isAuthenticated.value && _user.value && _user.value.superAdmin);

    // Grant 2026-11-02: whether Tappy (the help chat) is open to this person is the SERVER's answer,
    // `user.canUseAssistant`, computed per request from the super admin audience switches
    // (AssistantAccessRules in SLL.Api) and refreshed with the user on every launch (updateLocalUser).
    // An older API build sends no such field: then the old club-admin rule stands.
    const canUseAssistant = computed(() => canUseTappy(_user.value, isAuthenticated.value));

    const isAdminOfClub = (clubId) => _user.value && _user.value.adminFor && _user.value.adminFor.indexOf(clubId) !== -1;

    const isMemberOfClub = (clubId) => _user.value && _user.value.memberOf && _user.value.memberOf.indexOf(clubId) !== -1;

    const isImpersonating = computed(() => impersonatingAccessToken.value !== null);

    /// USER VERIFICATION SHIT

    const userVerificationToken = ref({});
    const userVerificationError = ref({});
    const userVerificationSaveError = ref({});
    const userVerificationLoading = ref(false);
    const hasUserVerificationErrors = computed(() => userVerificationError.value && Object.keys(userVerificationError.value).length > 0);

    const getVerificationToken = async (token) => {
        const {httpRequest} = useHttpRequest();
        userVerificationLoading.value = true;
        userVerificationError.value = {};
        try {
            let res = await httpRequest(endpoints.auth.validateToken(token), {});
            userVerificationToken.value = res;
        } catch (e) {
            userVerificationError.value = e;
        } finally {
            userVerificationLoading.value = false;
        }
    };

    const acceptVerificationToken = async (token, data) => {
        const {httpRequest} = useHttpRequest();
        userVerificationLoading.value = true;
        userVerificationSaveError.value = {};
        try {
            let res = await httpRequest(endpoints.auth.acceptInvite(token, data), data);
            accessToken.value = res.accessToken;
            _user.value = res;
            // Store in cookies
            accessTokenCookie.value = res.accessToken;
            userCookie.value = sessionUserCookieValue(res);
            updateAuthHeaders();
            await updateLocalUser();

        } catch (e) {
            userVerificationSaveError.value = e;
        } finally {
            userVerificationLoading.value = false;
        }
    };

    const declineVerificationToken = async (token) => {
        const {httpRequest} = useHttpRequest();
        userVerificationLoading.value = true;
        userVerificationSaveError.value = {};
        try {
            let res = await httpRequest(endpoints.auth.rejectInvite(token), {});
            userVerificationToken.value = res;
        } catch (e) {
            userVerificationSaveError.value = e;
        } finally {
            userVerificationLoading.value = false;
        }
    };

    const service = {
        user,
        userRefreshed,
        authUnreachable,
        updateLocalUser,
        accessToken,
        impersonatingUser,
        impersonatingAccessToken,
        getAccessToken,
        isAuthenticated,
        isSuperUser,
        canUseAssistant,
        isAdminOfClub,
        isMemberOfClub,
        externalLogin,
        joinWithInviteLink,
        joinPending,
        joinError,
        login,
        register,
        logout,
        init,
        impersonateUser,
        stopImpersonating,
        isImpersonating,
        userVerificationToken,
        userVerificationError,
        userVerificationSaveError,
        userVerificationLoading,
        hasUserVerificationErrors,
        validateShortLivedToken,
        getVerificationToken,
        acceptVerificationToken,
        declineVerificationToken,
        forgotPassword,
        adoptSession,
        externalLoginDelete,
        externalLoginLink,
        adoptProof,
    };

    // The per-request fields above are plain properties. These read through to
    // the lazily built Crud instances, so `const {loginErrors} = useAuthService()`
    // builds the login request the moment a sign-in page asks for it and a
    // page that never does builds nothing. Same names and same refs as before.
    defineLazy(service, forgotPasswordApi, {
        forgotPasswordItem: 'item', forgotPasswordLoading: 'formPending',
        forgotPasswordErrors: 'formErrors', hasForgotPasswordErrors: 'hasFormErrors',
    });
    defineLazy(service, loginApi, {loginLoading: 'formPending', loginErrors: 'formErrors', hasLoginErrors: 'hasFormErrors'});
    defineLazy(service, loginVerificationApi, {
        loginVerificationItem: 'item', loginVerification: 'createItem', loginVerificationLoading: 'formPending',
        loginVerificationErrors: 'formErrors', hasLoginVerificationErrors: 'hasFormErrors',
    });
    defineLazy(service, registerVerificationApi, {
        registerVerificationItem: 'item', registerVerification: 'createItem', registerVerificationLoading: 'formPending',
        registerVerificationErrors: 'formErrors', hasRegisterVerificationErrors: 'hasFormErrors',
    });
    defineLazy(service, externalLoginApi, {
        externalLoginItem: 'item', externalLoginLoading: 'formPending',
        externalLoginErrors: 'formErrors', hasExternalLoginErrors: 'hasFormErrors',
    });
    defineLazy(service, externalLoginLinkApi, {
        externalLoginLinkItem: 'item', externalLoginLinkAction: 'createItem', externalLoginLinkLoading: 'formPending',
        externalLoginLinkErrors: 'formErrors', hasExternalLoginLinkErrors: 'hasFormErrors',
    });
    defineLazy(service, externalLoginDeleteApi, {
        externalLoginDeleteItem: 'item', externalLoginDeleteAction: 'deleteItem', externalLoginDeleteLoading: 'formPending',
        externalLoginDeleteErrors: 'formErrors', hasExternalLoginDeleteErrors: 'hasFormErrors',
    });
    defineLazy(service, registerApi, {
        registerLoading: 'formPending', registerErrors: 'formErrors', hasRegisterErrors: 'hasFormErrors', registerItem: 'item',
    });
    defineLazy(service, impersonateApi, {impersonateItem: 'item', impersonateLoading: 'itemPending', impersonateErrors: 'itemErrors'});
    return service;
}

// Whether the state's user and the `user` cookie name the same person. The
// cookie reads back as the parsed object or, should Nuxt leave it, the string.
function sameSessionUser(stateUser, cookieUser) {
    if (!stateUser?.id || !cookieUser) return false;
    let cookie = cookieUser;
    if (typeof cookie === 'string') {
        try {
            cookie = JSON.parse(cookie);
        } catch {
            return false;
        }
    }
    return !!cookie?.id && cookie.id === stateUser.id;
}

// Enumerable getters, so destructuring and `service.loginErrors` both work and
// each one hands back the one ref the Crud instance holds.
function defineLazy(target, api, fields) {
    for (const [name, field] of Object.entries(fields)) {
        Object.defineProperty(target, name, {
            enumerable: true,
            configurable: true,
            get: () => api()[field],
        });
    }
}
